import cloneDeep from 'lodash/cloneDeep';
import {
  directRegistersByShipment,
  mixtureSubstancesBySubstance,
  shipmentsByRawMaterial,
  substancesWithRegistersByShipment
} from 'selectors/generic';
import store from 'source/store';
import theme from 'source/theme';
import { newLogoReachOk64 } from '../base64Images';
import { shortDate as dateFormater } from 'utils/helperFunctions/dateFormat';

const dataMapping = [
  { id: 'name', name: 'Substância' },
  { id: 'ec', name: 'EC' },
  { id: 'cas', name: 'CAS' },
  { id: 'svhc_description', name: 'Descrição', nested: 'echas' },
  { id: 'svhc_inclusion_reasons', name: 'Razão', nested: 'echas' },
  { id: 'svhc_inclusion_date', name: 'Data', nested: 'echas' },
  { id: 'authorization_dates', name: 'Data limite', nested: 'echas' },
  { id: 'authorization_exempted_uses', name: 'Isenções', nested: 'echas' },
  { id: 'restriction_descriptions', name: 'Descrição', nested: 'echas' },
  { id: 'restriction_entry_numbers', name: 'Entrada', nested: 'echas' }
];


const rowGenerator = (dataMapping, data, rawMaterialsById) => {
  const rows = [];
  // Cloning the data to avoid modification of Echas on redux store
  const clonedData = cloneDeep(data);

  // The four lists this report used to read off the payload: a raw material's
  // shipments, the mixtures a substance belongs to, and - per shipment - its
  // own registrations and the registrations of the substances inside it.
  const state = store.getState();
  const shipmentsOf = shipmentsByRawMaterial(state);
  const mixturesOf = mixtureSubstancesBySubstance(state);
  const directRegistersOf = directRegistersByShipment(state);
  const substancesWithRegistersOf = substancesWithRegistersByShipment(state);
  const suppliersById = state.suppliers.models;

  const nestedShipmentsCount = rawMaterial => {
    const mixtureIds = (mixturesOf[rawMaterial.id] || []).map(mix => mix.mixture_id);
    return mixtureIds.map(id => {
        return (shipmentsOf[id] || []).length;
      }
    ).reduce((acc, cv) => acc + cv, 0);
  };

  const firstRowGenerationSingleRestricao = (rawMaterial, echa) => {
    let echasPlusEntries = rawMaterial['echas'].length; // Calculates number of spanned rows
    rawMaterial['echas'].forEach(echa => {
      if (echa.restriction_entry_numbers.length > 1) {
        echasPlusEntries = echasPlusEntries + echa.restriction_entry_numbers.length - 1;
      }
    });
    return dataMapping.map(field => {
      if (field.nested) {
        return { text: echa[field.id], fillColor: '#EEEEEE' };
      } else {
        if (field.id === 'name') {
          return {
            text: rawMaterial[field.id],
            rowSpan: echasPlusEntries + (shipmentsOf[rawMaterial.id] || []).length + nestedShipmentsCount(rawMaterial),
            fillColor: theme.palette.primary.light
          };
        } else {
          return {
            text: rawMaterial[field.id],
            rowSpan: echasPlusEntries,
            fillColor: '#EEEEEE'
          };
        }
      }
    });
  };

  const firstRowGenerationMultipleRestricoes = (rawMaterial, echa) => {
    let echasPlusEntries = rawMaterial['echas'].length; // Calculates number of spanned rows
    rawMaterial['echas'].forEach(echa => {
      if (echa.restriction_entry_numbers.length > 1) {
        echasPlusEntries = echasPlusEntries + echa.restriction_entry_numbers.length - 1;
      }
    });
    return dataMapping.map(field => {
      if (field.nested) {
        return field.id === 'restriction_descriptions' || field.id === 'restriction_entry_numbers' ?
          { text: echa[field.id][0], fillColor: '#EEEEEE' } :
          {
            text: echa[field.id],
            rowSpan: echa.restriction_entry_numbers.length,
            fillColor: '#EEEEEE'
          };
      } else {
        if (field.id === 'name') {
          return {
            text: rawMaterial[field.id],
            rowSpan: echasPlusEntries + (shipmentsOf[rawMaterial.id] || []).length + nestedShipmentsCount(rawMaterial),
            fillColor: theme.palette.primary.light
          };
        } else {
          return {
            text: rawMaterial[field.id],
            rowSpan: echasPlusEntries,
            fillColor: '#EEEEEE'
          };
        }
      }
    });
  };

  // mixture is optional and only used when the shipment to be pushed is from
  // a substance that belongs to a mixture
  const pushShipment = (substance, shipment, mixture = null) => {
    let regs = directRegistersOf[shipment.id] || [];
    regs = regs.length > 0 ? regs[0] : {};
    if (mixture) {
      regs = (substancesWithRegistersOf[shipment.id] || []).find(shp => shp.substance_id === substance.id);
      regs = (regs && regs.supplier_registers.length > 0) ? regs.supplier_registers[0] : {};
    }
    rows.push(
      [
        { text: '', border: [false, false, false, false] },
        { text: mixture ? mixture.code : substance.code, colSpan: 1 },
        { text: shipment.trade_name, colSpan: 2 },
        {},
        { text: shipment.supplier_id ? suppliersById[shipment.supplier_id].name : '', colSpan: 1 },
        { text: regs.registration_number, colSpan: 1 },
        { text: dateFormater(regs.received_on), colSpan: 1 },
        { text: regs.exposure_scenario },
        { text: regs.observations, colSpan: 2 },
        {}
      ]
    );
  };

  clonedData.forEach(item => {
    if (item['echas']) {
      item['echas'].forEach((echa, index) => {

        if (index === 0) { // In the first Echa match

          if (echa.restriction_entry_numbers.length <= 1) {
            rows.push(firstRowGenerationSingleRestricao(item, echa));
          } else {
            echa.restriction_entry_numbers.forEach((re, i) => {
              if (i === 0) {
                rows.push(firstRowGenerationMultipleRestricoes(item, echa));
              } else {
                rows.push([
                  {},
                  { text: '', fillColor: '#EEEEEE' },
                  { text: '', fillColor: '#EEEEEE' },
                  { text: '', fillColor: '#EEEEEE' },
                  { text: '', fillColor: '#EEEEEE' },
                  { text: '', fillColor: '#EEEEEE' },
                  { text: '', fillColor: '#EEEEEE' },
                  { text: '', fillColor: '#EEEEEE' },
                  { text: echa.restriction_descriptions[i], fillColor: '#EEEEEE' },
                  { text: echa.restriction_entry_numbers[i], fillColor: '#EEEEEE' }]);
              }
            });
          }
          // TODO Not sure if this case happens
        } else { // If there are more echa matches. Are there multiple matches on the
          if (echa.restriction_entry_numbers.length <= 1) {
            rows.push(dataMapping.map(field => field.nested && echa[field.id]));
          } else {
            echa.restriction_entry_numbers.forEach((re, index) => {
              rows.push(
                dataMapping.map(field => field.id === 'restriction_descriptions' || field.id === 'restriction_entry_numbers' ?
                  { text: echa[field.id][index], fillColor: '#EEEEEE' } :
                  { text: echa[field.id], rowSpan: index === 0 ? echa.restriction_entry_numbers.length : 1, fillColor: '#EEEEEE' }
                )
              );
            });
          }
        }
      });
    } else {
      rows.push(
        dataMapping.map(field => field.id === 'name' ?
          {
            text: item[field.id],
            //rowSpan: 1 + item.raw_material_suppliers.length + nestedShipmentsCount(item),
            fillColor: '#EEEEEE',
            border: [false, true, false, false]
          } :
          { text: item[field.id], fillColor: '#EEEEEE' }
        )
      );
    }

    const itemShipments = shipmentsOf[item.id] || [];
    if (itemShipments.length > 0) {
      itemShipments.forEach(supplier => pushShipment(item, supplier));
    }

    const itemMixtures = mixturesOf[item.id] || [];
    if (itemMixtures.length > 0) {
      itemMixtures.forEach(mixture => {
        const mixtureShipments = shipmentsOf[mixture.mixture_id] || [];
        if (mixtureShipments.length > 0) {
          mixtureShipments.forEach(supplier =>
            pushShipment(item, supplier, rawMaterialsById[mixture.mixture_id])
          );
        }
      });

    }
  });
  return rows;
};

// playground requires you to assign document definition to a variable called dd

const dd = (data, rawMaterialsById) => {
  console.log('rowGenerator(data, extraData)', rowGenerator(dataMapping, data, rawMaterialsById));

  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [40, 60, 40, 40],

    footer: function (currentPage, pageCount) {
      return {
        text: `${dateFormater(new Date())} - ${currentPage.toString()} de ${pageCount}`,
        alignment: 'right',
        relativePosition: { x: -15, y: 20 },
        fontSize: 7
      };
    },
    header: function (currentPage, pageCount, pageSize) {
      return [
        {
          image: newLogoReachOk64,
          width: 50,
          relativePosition: { x: 20, y: 10 }
        }
      ];
    },
    content: [
      {
        style: 'header',
        text: 'R - Inventário ECHA' //title,
      },
      {
        style: 'tableExample',
        layout: 'lightHorizontalLines',
        table: {
          headerRows: 3,
          widths: [80, 50, 50, 90, 100, 40, 40, 50, 90, 20],
          body: [
            [
              { text: 'Substância', rowSpan: 2, fillColor: '#EEEEEE', border: [false, false, false, false] },
              { text: 'EC', rowSpan: 2, fillColor: '#EEEEEE' },
              { text: 'CAS', rowSpan: 2, fillColor: '#EEEEEE' },
              { text: 'SVHC', colSpan: 3, alignment: 'center', fillColor: '#EEEEEE' },
              { text: '', fillColor: '#EEEEEE' },
              { text: '', fillColor: '#EEEEEE' },
              { text: 'Autorização', colSpan: 2, alignment: 'center', fillColor: '#EEEEEE' },
              { text: '', fillColor: '#EEEEEE' },
              { text: 'Restrições', colSpan: 2, alignment: 'center', fillColor: '#EEEEEE' },
              { text: '', fillColor: '#EEEEEE' }
            ],
            [
              { text: '', fillColor: '#EEEEEE' },
              { text: '', fillColor: '#EEEEEE' },
              { text: '', fillColor: '#EEEEEE' },
              { text: 'Descrição', alignment: 'center', fillColor: '#EEEEEE' },
              { text: 'Razão', alignment: 'center', fillColor: '#EEEEEE' },
              { text: 'Data', alignment: 'center', fillColor: '#EEEEEE' },
              { text: 'Data limite', alignment: 'center', fillColor: '#EEEEEE' },
              { text: 'Isenções', alignment: 'center', fillColor: '#EEEEEE' },
              { text: 'Descrição', alignment: 'center', fillColor: '#EEEEEE' },
              { text: 'Nº', alignment: 'center', fillColor: '#EEEEEE' }
            ],
            [
              { text: '', border: [false, false, false, false] },
              { text: 'Codigo MP', colSpan: 1 },
              { text: 'Designação Comercial', colSpan: 2 },
              { text: '' },
              { text: 'Fornecedor', colSpan: 1 },
              { text: 'Nº Registo', colSpan: 1 },
              { text: 'Data recepção CE', colSpan: 1 },
              { text: 'CE' },
              { text: 'Observações', colSpan: 2 },
              { text: '' }
            ],

            ...rowGenerator(dataMapping, data, rawMaterialsById)
          ]
        }
      }
    ],
    styles: {
      header: {
        color: theme.palette.primary.main,
        fontSize: 16,
        bold: true,
        alignment: 'center'
      },
      tableExample: {
        margin: [0, 15, 0, 0],
        fontSize: 7
      }
    }

  };
};

export default dd;
